import { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useChainId } from 'wagmi';
import { parseEther, parseUnits } from 'viem';
import { WalletConnect } from './components/WalletConnect';
import { ChainSwitcher } from './components/ChainSwitcher';
import { supportedChains, TOKEN_ADDRESSES, CONTRACT_ADDRESSES } from './config/chains';
import { analyzeWallet, formatReport } from './services/etherscan';

// Contract ABI - updated for new contract with ApeCoin support
const CONTRACT_ABI = [
  {
    name: 'payWithUSDC',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'walletAddresses', type: 'string[]' },
      { name: 'year', type: 'uint256' }
    ],
    outputs: []
  },
  {
    name: 'payWithETH',
    type: 'function',
    stateMutability: 'payable',
    inputs: [
      { name: 'walletAddresses', type: 'string[]' },
      { name: 'year', type: 'uint256' }
    ],
    outputs: []
  },
  {
    name: 'payWithApeCoin',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'walletAddresses', type: 'string[]' },
      { name: 'year', type: 'uint256' }
    ],
    outputs: []
  },
  {
    name: 'PaymentReceived',
    type: 'event',
    inputs: [
      { name: 'paymentId', type: 'bytes32', indexed: true },
      { name: 'payer', type: 'address', indexed: true },
      { name: 'amount', type: 'uint256' },
      { name: 'paymentType', type: 'uint8' },
      { name: 'walletAddresses', type: 'string[]' },
      { name: 'year', type: 'uint256' }
    ]
  }
] as const;

const ERC20_ABI = [
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: [{ name: '', type: 'bool' }]
  }
] as const;

function App() {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const [walletAddresses, setWalletAddresses] = useState('');
  const [year, setYear] = useState('2026');
  const [paymentMethod, setPaymentMethod] = useState<'eth' | 'usdc' | 'apecoin'>('eth');
  const [status, setStatus] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'loading' } | null>(null);
  const [report, setReport] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalysisTriggered, setIsAnalysisTriggered] = useState(false);
  const [funFacts, setFunFacts] = useState<string[]>([]);
  const [currentFunFact, setCurrentFunFact] = useState<string>('');

  // Auto-select payment method based on chain
  useEffect(() => {
    if (chainId === 33139) {
      // ApeChain: only ApeCoin is available
      if (paymentMethod !== 'apecoin') {
        setPaymentMethod('apecoin');
      }
    } else if (chainId === 42161) {
      // Arbitrum One: only ETH and USDC available (no ApeCoin)
      if (paymentMethod === 'apecoin') {
        setPaymentMethod('eth');
      }
    } else if (chainId === 1) {
      // Ethereum Mainnet: only ETH and USDC available (no ApeCoin)
      if (paymentMethod === 'apecoin') {
        setPaymentMethod('eth');
      }
    } else if (chainId !== undefined && chainId !== 33139 && chainId !== 42161 && chainId !== 1) {
      // Other chains: switch from ApeCoin to ETH if needed (unless already set to USDC)
      if (paymentMethod === 'apecoin') {
        setPaymentMethod('eth');
      }
    }
  }, [chainId]); // Only depend on chainId to avoid infinite loops
  
  // Load fun facts on component mount
  useEffect(() => {
    fetch('/animal-facts.json')
      .then(res => res.json())
      .then(data => {
        setFunFacts(data);
        if (data.length > 0) {
          setCurrentFunFact(data[Math.floor(Math.random() * data.length)]);
        }
      })
      .catch(err => {
        console.warn('Failed to load fun facts:', err);
      });
  }, []);
  
  // Rotate fun facts every 10 seconds when loading
  useEffect(() => {
    if (status?.type === 'loading' && funFacts.length > 0) {
      const interval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * funFacts.length);
        setCurrentFunFact(funFacts[randomIndex]);
      }, 10000); // Change every 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [status?.type, funFacts]); // Prevent duplicate calls

  const { writeContract, data: hash, isPending } = useWriteContract();
  const { writeContract: writeApprove, data: approveHash, isPending: isApproving } = useWriteContract();
  const [pendingPayment, setPendingPayment] = useState<{ method: 'usdc' | 'apecoin'; addresses: string[]; year: string } | null>(null);

  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash: hash,
  });

  const { isLoading: isApprovalConfirming, isSuccess: isApprovalConfirmed } = useWaitForTransactionReceipt({
    hash: approveHash,
  });

  // Check if current chain is supported
  const isSupportedChain = supportedChains.some(chain => chain.id === chainId);
  const currentChain = supportedChains.find(chain => chain.id === chainId);
  const contractAddress = CONTRACT_ADDRESSES[chainId];
  const tokenAddresses = TOKEN_ADDRESSES[chainId] || { USDC: '', APECOIN: '' };

  const isProcessing = isPending || isConfirming || isApproving || isApprovalConfirming;

  async function getETHPrice(): Promise<number> {
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
      const data = await response.json();
      return data.ethereum?.usd || 3000;
    } catch (error) {
      console.warn('Failed to fetch ETH price, using default:', error);
      return 3000;
    }
  }

  async function getApeCoinPrice(): Promise<number> {
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=apecoin&vs_currencies=usd');
      const data = await response.json();
      return data.apecoin?.usd || 1;
    } catch (error) {
      console.warn('Failed to fetch ApeCoin price, using default:', error);
      return 1;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!isConnected || !address) {
      setStatus({ message: 'Please connect your wallet first', type: 'error' });
      return;
    }

    if (!isSupportedChain) {
      setStatus({ message: 'Please switch to a supported chain (Ethereum, Arbitrum, or ApeChain)', type: 'error' });
      return;
    }

    if (!contractAddress || contractAddress === '') {
      setStatus({ message: 'Contract not deployed on this chain yet. Please switch to a deployed chain.', type: 'error' });
      return;
    }

    const addresses = walletAddresses
      .split('\n')
      .map(addr => addr.trim())
      .filter(addr => addr.length > 0);

    if (addresses.length === 0) {
      setStatus({ message: 'Please enter at least one wallet address', type: 'error' });
      return;
    }

    if (addresses.length > 10) {
      setStatus({ message: 'Maximum 10 wallet addresses allowed', type: 'error' });
      return;
    }

    // Validate addresses
    for (const addr of addresses) {
      if (!addr.match(/^0x[a-fA-F0-9]{40}$/)) {
        setStatus({ message: `Invalid wallet address: ${addr}`, type: 'error' });
        return;
      }
    }

    try {
      setStatus({ message: 'Processing payment...', type: 'loading' });

      if (paymentMethod === 'eth') {
        // Calculate $1 worth of ETH
        const ethPrice = await getETHPrice();
        const ethAmount = parseEther((1 / ethPrice).toFixed(18));
        
        writeContract({
          address: contractAddress as `0x${string}`,
          abi: CONTRACT_ABI,
          functionName: 'payWithETH',
          args: [addresses, BigInt(year)],
          value: ethAmount,
        });
      } else if (paymentMethod === 'usdc') {
        // Approve USDC first ($1 = 1 USDC)
        const usdcAmount = parseUnits('1', 6); // 1 USDC with 6 decimals
        
        if (!tokenAddresses.USDC) {
          setStatus({ message: 'USDC token address not configured for this chain', type: 'error' });
          return;
        }

        setStatus({ message: 'Approving USDC...', type: 'info' });
        setPendingPayment({ method: 'usdc', addresses, year });
        writeApprove({
          address: tokenAddresses.USDC as `0x${string}`,
          abi: ERC20_ABI,
          functionName: 'approve',
          args: [contractAddress as `0x${string}`, usdcAmount],
        });
      } else if (paymentMethod === 'apecoin') {
        // On ApeChain, ApeCoin is the native currency, so use native payment
        // On other chains, ApeCoin is an ERC20 token, so use ERC20 transfer
        if (chainId === 33139) {
          // ApeChain: Use native payment (ApeCoin is native)
          const apeCoinPrice = await getApeCoinPrice();
          const apeAmount = parseEther((1 / apeCoinPrice).toFixed(18));
          
          writeContract({
            address: contractAddress as `0x${string}`,
            abi: CONTRACT_ABI,
            functionName: 'payWithETH', // This handles native currency on any chain
            args: [addresses, BigInt(year)],
            value: apeAmount,
          });
        } else {
          // Other chains: ApeCoin is ERC20, so approve and transfer
          const apeCoinPrice = await getApeCoinPrice();
          const apeAmount = parseUnits((1 / apeCoinPrice).toFixed(18), 18);
          
          if (!tokenAddresses.APECOIN) {
            setStatus({ message: 'ApeCoin token address not configured for this chain', type: 'error' });
            return;
          }

          setStatus({ message: 'Approving ApeCoin...', type: 'info' });
          setPendingPayment({ method: 'apecoin', addresses, year });
          writeApprove({
            address: tokenAddresses.APECOIN as `0x${string}`,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [contractAddress as `0x${string}`, apeAmount],
          });
        }
      }
    } catch (error: any) {
      console.error('Error:', error);
      setStatus({ message: `Error: ${error.message}`, type: 'error' });
    }
  }

  // Handle approval confirmation - proceed with payment
  useEffect(() => {
    if (isApprovalConfirmed && pendingPayment && contractAddress) {
      setStatus({ message: 'Approval confirmed. Processing payment...', type: 'info' });
      
      if (pendingPayment.method === 'usdc') {
        writeContract({
          address: contractAddress as `0x${string}`,
          abi: CONTRACT_ABI,
          functionName: 'payWithUSDC',
          args: [pendingPayment.addresses, BigInt(pendingPayment.year)],
        });
      } else if (pendingPayment.method === 'apecoin') {
        writeContract({
          address: contractAddress as `0x${string}`,
          abi: CONTRACT_ABI,
          functionName: 'payWithApeCoin',
          args: [pendingPayment.addresses, BigInt(pendingPayment.year)],
        });
      }
      
      setPendingPayment(null);
    }
  }, [isApprovalConfirmed, pendingPayment, contractAddress, writeContract]);

  // Handle transaction confirmation - generate report after payment
  useEffect(() => {
    if (isConfirmed && hash && !isAnalysisTriggered) {
      setIsAnalysisTriggered(true); // Prevent duplicate calls
      setStatus({ message: 'Payment successful! Generating report...', type: 'info' });
      generateReport();
    }
  }, [isConfirmed, hash, isAnalysisTriggered]);

  async function generateReport() {
    const addresses = walletAddresses
      .split('\n')
      .map(addr => addr.trim())
      .filter(addr => addr.length > 0 && addr.match(/^0x[a-fA-F0-9]{40}$/));

    if (addresses.length === 0) {
      setStatus({ message: 'No valid wallet addresses', type: 'error' });
      return;
    }

    try {
      setStatus({ message: 'Fetching transaction data...', type: 'loading' });
      
      // Analyze all wallets using Etherscan API directly
      const walletResults: any[] = [];
      for (const address of addresses) {
        const result = await analyzeWallet(chainId, address, parseInt(year));
        walletResults.push(result);
      }

      // Format the report
      setStatus({ message: 'Please wait, compiling data...', type: 'loading' });
      const reportText = await formatReport(walletResults, addresses, parseInt(year), chainId);

      setReport({
        text: reportText,
        data: walletResults,
        reportId: hash || `report-${Date.now()}`
      });

      setStatus({ message: 'Report generated successfully! Generating AI analysis...', type: 'success' });
      
      // Trigger AI analysis after report is generated (only if not already analyzing)
      if (!isAnalyzing) {
        const addressesForAnalysis = addresses;
        const yearForAnalysis = parseInt(year);
        // Use setTimeout to ensure state is updated before calling
        setTimeout(() => {
          analyzeWithPerplexity(reportText, walletResults, addressesForAnalysis, yearForAnalysis);
        }, 100);
      }
    } catch (error: any) {
      console.error('Error generating report:', error);
      setStatus({ message: `Error generating report: ${error.message || 'Network error. Please try again.'}`, type: 'error' });
    }
  }

  // Analyze transactions with Perplexity AI
  async function analyzeWithPerplexity(reportText: string, _walletResults: any[], _addresses: string[], _year: number) {
    // Prevent duplicate calls - check if already analyzing
    if (isAnalyzing) {
      console.log('AI analysis already in progress, skipping duplicate call');
      return;
    }
    
    setIsAnalyzing(true);
    setAiAnalysis(null);
    
    try {
      // Prepare report text for backend API - limit report text to avoid token limits
      // Truncate report if too long (keep it under ~15000 characters for prompt)
      const truncatedReport = reportText.length > 15000 
        ? reportText.substring(0, 15000) + '\n\n... (Report truncated for analysis)'
        : reportText;

      // Call backend API endpoint (keeps Perplexity API key secure on server-side)
      // The backend endpoint handles the Perplexity API call
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 65000); // 65 seconds (slightly longer for backend processing)
      
      try {
        // Call our backend API endpoint instead of Perplexity directly
        const response = await fetch('/api/analyze-ai', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reportText: truncatedReport
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          
          // Handle specific error codes
          if (response.status === 401 || response.status === 403) {
            throw new Error('Invalid API key. Please check your Perplexity API key configuration.');
          } else if (response.status === 429) {
            throw new Error('Rate limit exceeded. Please try again in a few moments.');
          } else if (response.status === 400) {
            throw new Error(`Invalid request: ${errorData.error || response.statusText}`);
          } else if (response.status === 504) {
            throw new Error('Request timeout. The analysis is taking longer than expected. Please try again with a shorter report period.');
          } else {
            throw new Error(`API error (${response.status}): ${errorData.error || response.statusText}`);
          }
        }

        const data = await response.json();
        
        // Backend returns { analysis: "..." }
        const analysisText = data.analysis || 'Unable to generate analysis. Please try again.';
        
        if (analysisText === 'Unable to generate analysis. Please try again.') {
          console.warn('Backend API response structure unexpected:', data);
          throw new Error('Unexpected response format from backend API');
        }
        
        setAiAnalysis(analysisText);
        } catch (fetchError: any) {
          clearTimeout(timeoutId);
          
          if (fetchError.name === 'AbortError') {
            throw new Error('Request timeout (60 seconds). The analysis is taking longer than expected. Please try again with a shorter report period.');
          }
          throw fetchError;
        }
      } catch (error: any) {
        console.error('Error analyzing with Perplexity:', error);
        const errorMessage = error.message || 'Unknown error occurred. Please try again later.';
        setAiAnalysis(`⚠️ Error generating AI analysis: ${errorMessage}\n\nIf this issue persists, please check your internet connection and API key configuration.`);
      } finally {
        setIsAnalyzing(false);
      }
  }

  // Copy report to clipboard
  async function copyReportToClipboard() {
    if (!report) return;
    
    const reportText = report.text || JSON.stringify(report, null, 2);
    
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = reportText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  }

  // Download report as text file
  function downloadReportAsFile() {
    if (!report) return;
    
    const reportText = report.text || JSON.stringify(report, null, 2);
    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wallet-analysis-report-${year}-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0D1117' }}>
      <div className="flex-1" style={{ position: 'relative' }}>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8 min-h-screen" style={{ backgroundColor: '#0D1117' }}>
          {/* Header with Chain Switcher */}
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl mb-2 flex items-center gap-3" style={{ fontWeight: 700, color: '#E5E5E5', lineHeight: 1.2 }}>
                  <img 
                    src="/clutchpuppy28.png"
                    alt="Clutch Labs"
                    className="magnifying-glow"
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      objectFit: 'contain',
                      imageRendering: 'auto'
                    }}
                    onError={(e) => {
                      // Fallback to favicon if Clutch Puppy fails
                      (e.target as HTMLImageElement).src = '/favicon-32x32.png';
                      (e.target as HTMLImageElement).onerror = () => {
                        // Final fallback to emoji
                        (e.target as HTMLImageElement).style.display = 'none';
                        const fallback = document.createElement('span');
                        fallback.className = 'text-3xl sm:text-4xl lg:text-5xl magnifying-glow';
                        fallback.textContent = '💼';
                        (e.target as HTMLImageElement).parentElement?.insertBefore(fallback, e.target);
                      };
                    }}
                  />
                  <span>Wallet Analysis Service</span>
                </h1>
                <p className="text-sm sm:text-base" style={{ fontWeight: 400, color: 'rgba(229, 229, 229, 0.7)' }}>
                  Comprehensive blockchain transaction analysis reports - $1 per report
                </p>
              </div>
              <WalletConnect />
            </div>
            <ChainSwitcher />
          </div>

          {/* Main Card */}
          <div className="stat-card">
            <h2 className="text-xl sm:text-2xl mb-4" style={{ fontWeight: 700, color: '#E5E5E5' }}>
              Generate Your Report - $1
            </h2>
              <p className="mb-6 text-sm" style={{ fontWeight: 400, color: 'rgba(229, 229, 229, 0.7)' }}>
                {chainId === 33139 
                  ? 'Enter wallet address(es) and pay $1 in ApeCoin to generate a comprehensive transaction analysis report.'
                  : chainId === 42161 || chainId === 1
                  ? 'Enter wallet address(es) and pay $1 (USDC or ETH) to generate a comprehensive transaction analysis report.'
                  : 'Enter wallet address(es) and pay $1 (USDC, ETH, or ApeCoin) to generate a comprehensive transaction analysis report.'
                }
              </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontWeight: 600, color: '#E5E5E5' }}>
                  Wallet Address(es) *
                </label>
                <textarea
                  value={walletAddresses}
                  onChange={(e) => setWalletAddresses(e.target.value)}
                  placeholder="0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb&#10;0x8ba1f109551bD432803012645Hac136c22C9299"
                  rows={4}
                  required
                  className="input-primary w-full font-mono text-xs"
                />
                <small className="text-xs mt-1 block" style={{ color: 'rgba(229, 229, 229, 0.7)' }}>
                  Enter one address per line (max 10 addresses)
                </small>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontWeight: 600, color: '#E5E5E5' }}>
                  Year *
                </label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  required
                  className="input-primary w-full"
                >
                  {Array.from({ length: 11 }, (_, i) => 2020 + i).map(y => (
                    <option key={y} value={y.toString()}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ fontWeight: 600, color: '#E5E5E5' }}>
                  Payment Method * ($1)
                </label>
                
                <div className="flex gap-4 flex-wrap">
                  {/* ETH option - available on all chains except ApeChain */}
                  {chainId !== 33139 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="eth"
                        checked={paymentMethod === 'eth'}
                        onChange={() => setPaymentMethod('eth')}
                        className="cursor-pointer"
                      />
                      <img 
                        src="https://assets.coingecko.com/coins/images/279/large/ethereum.png" 
                        alt="ETH"
                        style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <span style={{ color: '#E5E5E5', fontWeight: 500 }}>ETH (~$1 worth)</span>
                    </label>
                  )}
                  {/* USDC option - available on all chains except ApeChain */}
                  {chainId !== 33139 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="usdc"
                        checked={paymentMethod === 'usdc'}
                        onChange={() => setPaymentMethod('usdc')}
                        className="cursor-pointer"
                      />
                      <img 
                        src="https://assets.coingecko.com/coins/images/6319/large/USD_Coin_icon.png" 
                        alt="USDC"
                        style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <span style={{ color: '#E5E5E5', fontWeight: 500 }}>USDC (1 USDC)</span>
                    </label>
                  )}
                  {/* ApeCoin option - only available on ApeChain */}
                  {chainId === 33139 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="apecoin"
                        checked={paymentMethod === 'apecoin'}
                        onChange={() => setPaymentMethod('apecoin')}
                        className="cursor-pointer"
                      />
                      <img 
                        src="/apecoin-logo.png" 
                        alt="ApeCoin"
                        style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        onError={(e) => {
                          // Fallback to CoinGecko if local logo not found
                          (e.target as HTMLImageElement).src = 'https://assets.coingecko.com/coins/images/24383/large/apecoin.jpg';
                        }}
                      />
                      <span style={{ color: '#E5E5E5', fontWeight: 500 }}>ApeCoin (~$1 worth)</span>
                      <span className="text-xs" style={{ color: 'rgba(229, 229, 229, 0.7)' }}>(Only option on ApeChain)</span>
                    </label>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={!isConnected || !isSupportedChain || isProcessing || !contractAddress}
                className="btn-primary w-full"
                style={{
                  opacity: isConnected && isSupportedChain && !isProcessing && contractAddress ? 1 : 0.6,
                  cursor: isConnected && isSupportedChain && !isProcessing && contractAddress ? 'pointer' : 'not-allowed'
                }}
              >
                {isProcessing ? 'Processing...' : 'Generate Report ($1)'}
              </button>
            </form>

            {status && (
              <div className={`mt-4 p-4 rounded-lg border ${
                status.type === 'success' ? 'bg-green-900/20 border-green-500' :
                status.type === 'error' ? 'bg-red-900/20 border-red-500' :
                status.type === 'info' ? 'bg-blue-900/20 border-blue-500' :
                'bg-yellow-900/20 border-yellow-500'
              }`} style={{ borderRadius: 'var(--radius-sm)' }}>
                <p className="text-sm" style={{ color: '#E5E5E5', fontWeight: 500 }}>{status.message}</p>
                {status.type === 'loading' && currentFunFact && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(229, 229, 229, 0.2)' }}>
                    <p className="text-xs mb-2" style={{ color: 'rgba(229, 229, 229, 0.8)', fontStyle: 'italic' }}>
                      While you are waiting...
                    </p>
                    <p className="text-sm" style={{ color: '#E5E5E5', fontWeight: 400 }}>
                      <span style={{ fontWeight: 600 }}>Did you know?</span> {currentFunFact}
                    </p>
                  </div>
                )}
              </div>
            )}

            {!contractAddress && isSupportedChain && (
              <div className="mt-4 p-4 rounded-lg border bg-yellow-900/20 border-yellow-500" style={{ borderRadius: 'var(--radius-sm)' }}>
                <p className="text-sm" style={{ color: '#E5E5E5', fontWeight: 500 }}>
                  ⚠️ Contract not deployed on {currentChain?.name} yet. Please deploy the contract first or switch to a deployed chain.
                </p>
              </div>
            )}
          </div>

          {/* Report Card */}
          {report && (
            <div className="stat-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl" style={{ fontWeight: 700, color: '#E5E5E5' }}>
                  Your Report
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={copyReportToClipboard}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: copied ? '#10b981' : '#2D2F36',
                      color: '#E5E5E5',
                      border: `1px solid ${copied ? '#10b981' : '#3D3F46'}`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      if (!copied) {
                        e.currentTarget.style.backgroundColor = '#3D3F46';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!copied) {
                        e.currentTarget.style.backgroundColor = '#2D2F36';
                      }
                    }}
                  >
                    {copied ? (
                      <>
                        <span>✓</span>
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={downloadReportAsFile}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: '#2D2F36',
                      color: '#E5E5E5',
                      border: '1px solid #3D3F46',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#3D3F46';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#2D2F36';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div className="mb-4 p-4 rounded-lg max-h-96 overflow-y-auto" style={{ backgroundColor: '#0D1117', borderRadius: 'var(--radius-sm)' }}>
                <pre className="text-xs sm:text-sm whitespace-pre-wrap font-mono" style={{ color: '#E5E5E5' }}>
                  {report.text || JSON.stringify(report, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* AI Analysis Dialogue Box */}
          {report && (
            <div className="stat-card mt-6">
              <div className="flex items-center gap-2 mb-4">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#8B5CF6' }}>
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
                <h2 className="text-xl sm:text-2xl" style={{ fontWeight: 700, color: '#E5E5E5' }}>
                  AI Trading Analysis
                </h2>
              </div>
              
              {isAnalyzing && (
                <div className="p-6 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#8B5CF6' }}></div>
                  <p className="mt-4 text-sm" style={{ color: 'rgba(229, 229, 229, 0.7)' }}>
                    Analyzing your trading patterns with AI...
                  </p>
                </div>
              )}

              {aiAnalysis && !isAnalyzing && (
                <div className="p-4 rounded-lg max-h-96 overflow-y-auto" style={{ backgroundColor: '#0D1117', borderRadius: 'var(--radius-sm)' }}>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: '#E5E5E5' }}>
                    {aiAnalysis.split('\n').map((line, index) => {
                      // Check if line is a header or section title
                      const isHeader = line.match(/^(Rating|Strengths|Weaknesses|Advice|Analysis|Summary|Recommendation)/i);
                      const isNumberedList = line.match(/^[0-9]+\./);
                      const isEmpty = line.trim() === '';
                      
                      return (
                        <p 
                          key={index} 
                          className={isEmpty ? 'mb-1' : 'mb-2'} 
                          style={{ 
                            color: isHeader ? '#8B5CF6' : isNumberedList ? '#9CA3AF' : '#E5E5E5',
                            fontWeight: isHeader ? 600 : 400,
                            fontSize: isHeader ? '1em' : '0.875rem'
                          }}
                        >
                          {line || '\u00A0'} {/* Non-breaking space for empty lines */}
                        </p>
                      );
                    })}
                  </div>
                </div>
              )}

              {!aiAnalysis && !isAnalyzing && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#0D1117', borderRadius: 'var(--radius-sm)' }}>
                  <p className="text-sm" style={{ color: 'rgba(229, 229, 229, 0.7)' }}>
                    AI analysis will appear here after your report is generated.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-12 sm:mt-20 py-6 sm:py-10 border-t" style={{ backgroundColor: '#0D1117', borderColor: '#2D2F36' }}>
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6">
            <div className="text-center">
              <p className="mb-4 sm:mb-6 text-xs sm:text-sm" style={{ fontWeight: 400, color: 'rgba(229, 229, 229, 0.7)' }}>
                Built on Ethereum • Arbitrum • ApeChain • Powered by Etherscan API & CoinGecko
              </p>
              <p className="text-sm sm:text-base glowing-text" style={{ fontWeight: 500, color: '#2081E2' }}>
                Powered by{' '}
                <a
                  href="https://x.com/ClutchMarkets"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glowing-text"
                  style={{
                    fontWeight: 700,
                    color: 'inherit',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  Clutch Labs
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;